import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Textarea } from './ui/textarea';
import { Copy, Check, Plus, Trash2, Users, TrendingUp, Link as LinkIcon, ExternalLink, Edit2, X, Save, Eye, DollarSign, Package, Percent } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AffiliateManagement = () => {
  const { token } = useAuth();
  const [affiliates, setAffiliates] = useState([]);
  const [newAffiliate, setNewAffiliate] = useState({
    name: '',
    description: '',
    commission_rate: 10
  });
  const [affiliateSearchQuery, setAffiliateSearchQuery] = useState('');
  const [copiedCode, setCopiedCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingAffiliate, setEditingAffiliate] = useState(null);
  const [viewingOrders, setViewingOrders] = useState(null);
  const [ordersDetails, setOrdersDetails] = useState(null);

  useEffect(() => {
    fetchAffiliates();
  }, []);

  const fetchAffiliates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/affiliates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAffiliates(data);
      }
    } catch (error) {
      console.error('Error fetching affiliates:', error);
    } finally {
      setLoading(false);
    }
  };

  const createAffiliate = async () => {
    if (!newAffiliate.name.trim()) {
      alert('Entrez un nom pour le streamer/affilié');
      return;
    }

    const cleanName = newAffiliate.name.toLowerCase().replace(/[^a-z0-9]/g, '');
    
    try {
      const response = await fetch(`${API_URL}/api/affiliates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newAffiliate.name.trim(),
          code: cleanName,
          description: newAffiliate.description.trim(),
          type: 'streamer',
          commission_rate: parseFloat(newAffiliate.commission_rate) || 10
        })
      });

      if (response.ok) {
        setNewAffiliate({ name: '', description: '', commission_rate: 10 });
        fetchAffiliates();
      } else {
        const error = await response.json();
        alert(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Error creating affiliate:', error);
      alert('Une erreur est survenue');
    }
  };

  const updateAffiliate = async (code, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/affiliates/${code}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        fetchAffiliates();
        setEditingAffiliate(null);
      } else {
        const error = await response.json();
        alert(error.detail || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Error updating affiliate:', error);
      alert('Une erreur est survenue');
    }
  };

  const assignPromo = async (code) => {
    const promo_code = (document.getElementById(`promo-code-${code}`)?.value || '').trim();
    const discount_percent = parseInt(document.getElementById(`promo-disc-${code}`)?.value || '5', 10);
    if (!promo_code) { alert('Entre un code promo'); return; }
    try {
      const res = await fetch(`${API_URL}/api/admin/affiliates/${code}/promo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ promo_code, discount_percent }),
      });
      if (res.ok) fetchAffiliates();
      else { const d = await res.json().catch(() => ({})); alert(d.detail || 'Erreur'); }
    } catch { alert('Erreur réseau'); }
  };

  const removePromo = async (code) => {
    if (!window.confirm('Retirer le code promo de cet affilié ?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/affiliates/${code}/promo`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) fetchAffiliates();
    } catch { alert('Erreur réseau'); }
  };

  const fetchAffiliateOrders = async (code) => {
    try {
      const response = await fetch(`${API_URL}/api/affiliates/${code}/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOrdersDetails(data);
        setViewingOrders(code);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const deleteAffiliate = async (code) => {
    if (!window.confirm(`Supprimer l'affilié "${code}" ?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/affiliates/${code}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchAffiliates();
      }
    } catch (error) {
      console.error('Error deleting affiliate:', error);
    }
  };

  const copyAffiliateLink = (code) => {
    const link = `${window.location.origin}/${code}`;
    navigator.clipboard.writeText(link);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const filteredAffiliates = affiliates.filter(aff => 
    aff.name.toLowerCase().includes(affiliateSearchQuery.toLowerCase()) ||
    aff.code.toLowerCase().includes(affiliateSearchQuery.toLowerCase())
  ).sort((a, b) => {
    // Les affiliés en attente d'un code promo remontent en haut de la liste
    const aPending = a.promo_requested && !a.promo_code ? 1 : 0;
    const bPending = b.promo_requested && !b.promo_code ? 1 : 0;
    return bPending - aPending;
  });

  const totalClicks = affiliates.reduce((sum, aff) => sum + (aff.clicks || 0), 0);
  const totalOrders = affiliates.reduce((sum, aff) => sum + (aff.orders || 0), 0);
  const totalRevenue = affiliates.reduce((sum, aff) => sum + (aff.revenue || 0), 0);
  const totalCommission = affiliates.reduce((sum, aff) => sum + (aff.commission || 0), 0);

  if (loading) {
    return <div className="text-center py-12 text-gray-400">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-cyan-900/20 to-cyan-950/20 border-cyan-800/30">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-cyan-600/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Total Affiliés</p>
                <p className="text-3xl font-bold text-white">{affiliates.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-900/20 to-orange-950/20 border-orange-800/30">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-orange-600/20 flex items-center justify-center">
                <LinkIcon className="w-6 h-6 text-orange-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Total Clics</p>
                <p className="text-3xl font-bold text-white">{totalClicks}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-900/20 to-green-950/20 border-green-800/30">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-green-600/20 flex items-center justify-center">
                <Package className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Total Ventes</p>
                <p className="text-3xl font-bold text-white">{totalOrders}</p>
                <p className="text-green-400 text-sm">{totalRevenue.toFixed(2)}€</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-900/20 to-purple-950/20 border-purple-800/30">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-purple-600/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Commissions</p>
                <p className="text-3xl font-bold text-white">{totalCommission.toFixed(2)}€</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create New Affiliate */}
      <div className="bg-[#0f1119] border border-cyan-900/30 rounded-xl p-6">
        <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5 text-cyan-400" />
          Créer un nouveau affilié
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-gray-400 text-sm mb-2 block">Nom *</label>
            <Input
              placeholder="Kamet0, Squeezie, Solary..."
              value={newAffiliate.name}
              onChange={(e) => setNewAffiliate({...newAffiliate, name: e.target.value})}
              className="bg-[#1a1b2e] border-cyan-900/30 text-white"
            />
          </div>
          <div>
            <label className="text-gray-400 text-sm mb-2 block">Commission (%) *</label>
            <Input
              type="number"
              placeholder="10"
              value={newAffiliate.commission_rate}
              onChange={(e) => setNewAffiliate({...newAffiliate, commission_rate: e.target.value})}
              className="bg-[#1a1b2e] border-cyan-900/30 text-white"
            />
          </div>
        </div>
        <div className="mb-4">
          <label className="text-gray-400 text-sm mb-2 block">Description (optionnel)</label>
          <Textarea
            placeholder="Streamer Warzone, 50k followers..."
            value={newAffiliate.description}
            onChange={(e) => setNewAffiliate({...newAffiliate, description: e.target.value})}
            className="bg-[#1a1b2e] border-cyan-900/30 text-white"
            rows={2}
          />
        </div>
        <Button 
          onClick={createAffiliate}
          className="bg-cyan-600 hover:bg-cyan-700 text-white w-full md:w-auto"
        >
          <Plus className="w-4 h-4 mr-2" />
          Créer l'affilié
        </Button>
        <p className="text-gray-500 text-sm mt-2">
          Code généré automatiquement (ex: "Kamet0" → "kamet0")
        </p>
      </div>

      {/* Affiliates List */}
      <div className="bg-[#0f1119] border border-cyan-900/30 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-white font-bold text-lg flex items-center gap-2 flex-wrap">
            Liste des Affiliés ({filteredAffiliates.length})
            {filteredAffiliates.filter(a => a.promo_requested && !a.promo_code).length > 0 && (
              <span data-testid="affiliate-pending-count" className="px-2 py-0.5 bg-amber-500/20 border border-amber-400/40 text-amber-300 text-xs font-semibold rounded-full animate-pulse">
                🔔 {filteredAffiliates.filter(a => a.promo_requested && !a.promo_code).length} demande(s) de code
              </span>
            )}
          </h3>
          <Input
            placeholder="🔍 Rechercher..."
            value={affiliateSearchQuery}
            onChange={(e) => setAffiliateSearchQuery(e.target.value)}
            className="bg-[#1a1b2e] border-cyan-900/30 text-white max-w-xs"
          />
        </div>
        
        {filteredAffiliates.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {affiliateSearchQuery ? 'Aucun affilié trouvé' : 'Aucun affilié créé. Commencez par en ajouter un!'}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredAffiliates.map((affiliate) => (
              <div
                key={affiliate.code}
                data-testid={`affiliate-card-${affiliate.code}`}
                className={`bg-[#1a1b2e] rounded-lg p-4 transition-colors ${
                  affiliate.promo_requested && !affiliate.promo_code
                    ? 'border-2 border-amber-400/70 ring-2 ring-amber-400/20'
                    : 'border border-cyan-900/20 hover:border-cyan-700/40'
                }`}
              >
                {affiliate.promo_requested && !affiliate.promo_code && (
                  <div
                    data-testid={`affiliate-promo-request-banner-${affiliate.code}`}
                    className="mb-3 flex flex-col sm:flex-row sm:items-center gap-1.5 bg-amber-500/15 border border-amber-400/40 rounded-lg px-3 py-2.5"
                  >
                    <span className="flex items-center gap-2 text-amber-300 font-semibold text-sm flex-1">
                      <span className="animate-pulse">🔔</span> {affiliate.name} a demandé un code promo
                    </span>
                    <span className="text-amber-200/70 text-xs">Attribue-le ci-dessous ⬇️</span>
                  </div>
                )}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-white font-semibold text-lg">{affiliate.name}</h4>
                      <span className="px-2 py-1 bg-cyan-950/50 rounded text-cyan-400 font-mono text-sm">
                        {affiliate.code}
                      </span>
                      <span className="px-2 py-1 bg-purple-950/50 rounded text-purple-400 text-xs font-semibold">
                        {affiliate.commission_rate}% commission
                      </span>
                    </div>
                    {affiliate.description && (
                      <p className="text-gray-400 text-sm mb-2">{affiliate.description}</p>
                    )}
                    <div className="flex items-center gap-4 text-sm text-gray-400">
                      <span>{affiliate.clicks || 0} clics</span>
                      <span>•</span>
                      <span className="text-green-400 font-semibold">{affiliate.orders || 0} ventes</span>
                      <span>•</span>
                      <span className="text-orange-400 font-semibold">{(affiliate.revenue || 0).toFixed(2)}€ CA</span>
                      <span>•</span>
                      <span className="text-purple-400 font-semibold">{(affiliate.commission || 0).toFixed(2)}€ commission</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingAffiliate(affiliate.code === editingAffiliate ? null : affiliate.code)}
                      className="text-gray-400 hover:text-white"
                    >
                      {editingAffiliate === affiliate.code ? <X className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => fetchAffiliateOrders(affiliate.code)}
                      className="text-blue-400 hover:text-blue-300"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteAffiliate(affiliate.code)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Edit Form */}
                {editingAffiliate === affiliate.code && (
                  <div className="border-t border-cyan-900/20 pt-4 mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <Input
                        placeholder="Nom"
                        defaultValue={affiliate.name}
                        id={`name-${affiliate.code}`}
                        className="bg-[#0a0b14] border-cyan-900/30 text-white text-sm"
                      />
                      <Input
                        type="number"
                        placeholder="Commission %"
                        defaultValue={affiliate.commission_rate}
                        id={`rate-${affiliate.code}`}
                        className="bg-[#0a0b14] border-cyan-900/30 text-white text-sm"
                      />
                      <Button
                        size="sm"
                        onClick={() => {
                          const name = document.getElementById(`name-${affiliate.code}`).value;
                          const rate = parseFloat(document.getElementById(`rate-${affiliate.code}`).value);
                          const desc = document.getElementById(`desc-${affiliate.code}`).value;
                          updateAffiliate(affiliate.code, { name, commission_rate: rate, description: desc });
                        }}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <Save className="w-4 h-4 mr-1" />
                        Sauvegarder
                      </Button>
                    </div>
                    <Textarea
                      placeholder="Description"
                      defaultValue={affiliate.description}
                      id={`desc-${affiliate.code}`}
                      className="bg-[#0a0b14] border-cyan-900/30 text-white text-sm"
                      rows={2}
                    />
                  </div>
                )}

                {/* Link Copy Section */}
                <div className="flex items-center gap-2 mt-4">
                  <div className="flex-1 bg-[#0a0b14] px-3 py-2 rounded border border-cyan-900/20 text-gray-400 text-sm font-mono truncate">
                    {window.location.origin}/{affiliate.code}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => copyAffiliateLink(affiliate.code)}
                    className="bg-cyan-700 hover:bg-cyan-600 text-white"
                  >
                    {copiedCode === affiliate.code ? (
                      <>
                        <Check className="w-4 h-4 mr-1" />
                        Copié
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        Copier
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => window.open(`${window.location.origin}/${affiliate.code}`, '_blank')}
                    className="text-gray-400 hover:text-white"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>

                {/* Code promo affilié */}
                <div className="mt-4 pt-4 border-t border-fuchsia-900/20">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-fuchsia-300 text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5">
                      <Percent className="w-3.5 h-3.5" /> Code promo
                    </span>
                    {affiliate.promo_requested && !affiliate.promo_code && (
                      <span className="px-2 py-0.5 bg-amber-500/15 text-amber-300 text-xs rounded-full">⏳ A demandé un code</span>
                    )}
                  </div>
                  {affiliate.promo_code ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-2 py-1 bg-fuchsia-950/50 rounded text-fuchsia-300 font-mono text-sm">{affiliate.promo_code}</span>
                      <span className="text-gray-400 text-sm">-{affiliate.promo_discount_percent || 5}% client</span>
                      <Button size="sm" variant="ghost" onClick={() => removePromo(affiliate.code)} className="text-red-400 hover:text-red-300 ml-auto">
                        Retirer
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Input
                        id={`promo-code-${affiliate.code}`}
                        placeholder="Ex: WAKKS5"
                        defaultValue={`${affiliate.code}5`.toUpperCase()}
                        className="bg-[#0a0b14] border-fuchsia-900/30 text-white text-sm w-32"
                      />
                      <Input
                        id={`promo-disc-${affiliate.code}`}
                        type="number"
                        defaultValue={5}
                        className="bg-[#0a0b14] border-fuchsia-900/30 text-white text-sm w-20"
                      />
                      <span className="text-gray-500 text-xs">% réduc</span>
                      <Button size="sm" onClick={() => assignPromo(affiliate.code)} className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white">
                        Attribuer
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Orders Modal */}
      {viewingOrders && ordersDetails && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f1119] border border-cyan-900/30 rounded-xl max-w-4xl w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-white font-bold text-xl">
                  Commandes de {ordersDetails.affiliate.name}
                </h3>
                <p className="text-gray-400 text-sm">
                  {ordersDetails.stats.total_orders} ventes • {ordersDetails.stats.total_revenue.toFixed(2)}€ CA • {ordersDetails.stats.total_commission.toFixed(2)}€ commission ({ordersDetails.affiliate.commission_rate}%)
                </p>
              </div>
              <Button
                variant="ghost"
                onClick={() => {
                  setViewingOrders(null);
                  setOrdersDetails(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="space-y-3">
              {ordersDetails.orders.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Aucune commande pour cet affilié</p>
              ) : (
                ordersDetails.orders.map((order) => (
                  <div key={order.id} className="bg-[#1a1b2e] border border-cyan-900/20 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-semibold">{order.service}</p>
                        <p className="text-gray-400 text-sm">{order.username} • {new Date(order.created_at).toLocaleDateString('fr-FR')}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-green-400 font-bold">{order.price.toFixed(2)}€</p>
                        <p className="text-purple-400 text-sm">Commission: {order.commission.toFixed(2)}€</p>
                        <span className={`inline-block px-2 py-1 rounded text-xs mt-1 ${
                          order.status === 'completed' ? 'bg-green-950/50 text-green-400' :
                          order.status === 'in_progress' ? 'bg-blue-950/50 text-blue-400' :
                          'bg-yellow-950/50 text-yellow-400'
                        }`}>
                          {order.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AffiliateManagement;
